<template>
  <transition
    :duration="1000"
    name="slide-fade"
  >
    <div
      v-if="isVisible"
      :class="{ 'modal--center': modalData.autoHeight }"
      aria-modal="true"
      class="modal"
      role="dialog"
    >
      <div
        class="modal__backdrop"
        @click="userClose"
      />

      <div
        :class="{ 'modal__dialog-wrapper--static': modalData.autoHeight }"
        class="modal__dialog-wrapper"
      >
        <button
          v-if="modalData.isClosable"
          aria-label="Fechar"
          class="modal__close-btn"
          title="Fechar"
          @click="userClose"
        >
          <img src="~/assets/images/icons/i-close.svg" class="breadcrumb-block__list-item__icon">
        </button>

        <div
          ref="dialog"
          :class="{ 'modal__dialog--full-height': modalData.fullHeight,
                    'modal__dialog--auto-height': modalData.autoHeight }"
          class="modal__dialog"
        >
          <component
            :is="modalData.content"
            v-bind="modalData.props"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<style lang="scss" scoped>
  @import '~/assets/stylesheets/utils/module.sass';

.modal {
  align-items: flex-end;
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  padding-left: 0;
  padding-right: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 3;

  @media (min-width: $screen-md-min) {
    align-items: center;
  }
}

.modal--center {
  align-items: center;
}

.modal__backdrop {
  background-color: rgba(51,51,51,0.72);
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
}

.modal__dialog-wrapper {
  pointer-events: none;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  left: 0;
  max-width: $screen-lg;
  position: fixed;
  right: 0;
  top: 0;

  @media (min-width: $screen-md-min) {
    padding-left: $space-m;
    padding-right: $space-m;
    position: static;
  }
}

.modal__dialog-wrapper--static {
  position: static;
}

.modal__dialog {
  pointer-events: auto;
  background-color: $white;
  height: calc(100% - 48px);
  max-width: 100vw;
  overflow-y: auto;
  position: relative;
  width: 100vw;

  @media (min-width: $screen-md-min) {
    border-radius: $border-radius;
    height: auto;
    max-height: 90vh;
    padding-left: 0;
    padding-right: 0;
    width: auto;
  }

  @media (min-width: $screen-lg-min) {
    max-width: $screen-lg;
  }
}

.modal__dialog--full-height {
  @media (min-width: $screen-md-min) {
    height: 90vh;
  }
}

.modal__dialog--auto-height {
  background-color: transparent;
  height: auto;
  overflow-y: hidden;
  width: auto;
}

.modal__close-btn {
  align-self: flex-end;
  background: none;
  border: 0;
  color: $white;
  cursor: pointer;
  height: 16px;
  margin-bottom: $space-m;
  margin-right: $space-m;
  padding: 0;
  pointer-events: auto;
  width: 16px;
  z-index: 3;

  @media (min-width: $screen-md-min) {
    height: 24px;
    margin-right: 0;
    width: 24px;
  }
}

/* Vue animations */
.slide-fade-enter-active {
  transition: all .3s ease;
}

.slide-fade-leave-active {
  transition: all .1s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>

<script>
import { clearAllBodyScrollLocks, disableBodyScroll } from '~/assets/js/utils/scroll';
import { mapActions, mapState } from 'vuex';
import Vue from 'vue';

const ESC_BUTTON = 'Escape';

export default {
  name: 'Modal',
  computed: {
    ...mapState('modal', {
      isVisible: 'isVisible',
      modalData: ({ modalData }) => ({
        content: 'modalName',
        autoHeight: false,
        fullHeight: false,
        isClosable: true,
        props: {},
        ...modalData,
      }),
    }),
  },
  watch: {
    isVisible(isVisible) {
      if (isVisible) {
        const {
          modalData: { content },
          $refs: { dialog },
        } = this;
        const kebabContent = convertToKebabCase(content);
        Vue.component(content, () => import(`~/components/modals/${kebabContent}.vue`));
        disableBodyScroll(dialog);
      } else {
        clearAllBodyScrollLocks();
      }
    },
  },
  mounted() {
    window.addEventListener('keydown', this.closeModalOnKeypress);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.closeModalOnKeypress);
  },
  methods: {
    ...mapActions('modal', {
      close: 'close'
    }),
    closeModalOnKeypress({ key }) {
      if (key === ESC_BUTTON) {
        this.userClose();
      }
    },
    userClose() {
      if (this.modalData.isClosable) {
        this.close();
      }
    },
    convertToKebabCase(input) {
      const kebabCase = input
        .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
        .join('-')
        .toLowerCase();
      return kebabCase
    }
  },
};
</script>
